"use client";

import { Slot } from "@radix-ui/react-slot";
import { type AriaButtonOptions, useButton } from "@react-aria/button";
import { useObjectRef } from "@react-aria/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, type Ref } from "react";

import { cn } from "../../utils/cn.util";

export const buttonVariants = cva(
  "font-semibold flex items-center justify-center gap-2 whitespace-nowrap rounded-lg transition-colors focus-visible:outline-none disabled:cursor-not-allowed",
  {
    variants: {
      color: {
        primary: "focus-visible:shadow-button-primary",
        secondary: "focus-visible:shadow-button-secondary",
        error: "focus-visible:shadow-button-error",
      },
      variant: {
        filled: "",
        light: "",
        outline: "border",
        ghost: "bg-transparent focus-visible:shadow-none",
        link: "bg-transparent !p-0",
      },
      size: {
        // 2xs ONLY for icon have issue to refactor
        "2xs": "",
        xxs: "h-5 px-2 text-xs font-normal",
        xs: "h-8 px-2.5 text-xs font-normal",
        sm: "h-9 px-3.5 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-[18px] text-base",
        xl: "h-12 px-5 text-base",
        "2xl": "h-14 px-[28px] text-base",
      },
    },
    defaultVariants: {
      color: "primary",
      variant: "filled",
      size: "sm",
    },
    compoundVariants: [
      {
        variant: "link",
        size: ["xs", "sm", "md", "lg", "xl", "2xl"],
        className: "h-[unset] focus-visible:ring-0",
      },
      {
        color: "primary",
        variant: "filled",
        className: ["text-white bg-primary-50", "enabled:hover:bg-primary-40", "disabled:bg-primary-90"],
      },
      {
        color: "secondary",
        variant: "filled",
        className: [],
      },
      {
        color: "error",
        variant: "filled",
        className: ["text-white bg-error-50", "enabled:hover:bg-error-40", "disabled:bg-error-90"],
      },
      {
        color: "primary",
        variant: "light",
        className: [
          "text-primary-40 bg-primary-95",
          "enabled:hover:bg-primary-90",
          "disabled:bg-primary-98 disabled:text-primary-80",
        ],
      },
      {
        color: "secondary",
        variant: "light",
        className: [],
      },
      {
        color: "error",
        variant: "light",
        className: [
          "text-error-50 bg-error-95",
          "enabled:hover:bg-error-90",
          "disabled:bg-error-95 disabled:text-error-80",
        ],
      },
      {
        color: "primary",
        variant: "outline",
        className: [],
      },
      {
        color: "secondary",
        variant: "outline",
        className: [
          "border-neutral-80 text-neutral-30 bg-white",
          "enabled:hover:bg-neutral-95",
          "disabled:text-neutral-80 disabled:border-neutral-90",
        ],
      },
      {
        color: "error",
        variant: "outline",
        className: [
          "border-error-60 text-error-50",
          "enabled:hover:bg-error-95",
          "disabled:text-error-90 border-error-90",
        ],
      },
      {
        color: "primary",
        variant: "ghost",
        className: ["text-primary-40", "enabled:hover:bg-primary-95", "disabled:text-neutral-80"],
      },
      {
        color: "secondary",
        variant: "ghost",
        className: [
          "text-neutral-40",
          "enabled:hover:bg-neutral-95",
          "disabled:text-neutral-80",
          "focus-visible:ring-1",
        ],
      },
      {
        color: "error",
        variant: "ghost",
        className: ["text-error-50", "enabled:hover:bg-error-95", "disabled:text-error-80"],
      },
      {
        color: "primary",
        variant: "link",
        className: ["text-primary-50", "enabled:hover:text-primary-20", "disabled:text-neutral-80"],
      },
      {
        color: "secondary",
        variant: "link",
        className: ["text-neutral-50", "enabled:hover:text-neutral-40", "disabled:text-neutral-80"],
      },
      {
        color: "error",
        variant: "link",
        className: ["text-error-50", "enabled:hover:text-error-40", "disabled:text-error-80"],
      },
    ],
  }
);

const iconButtonVariants = cva("shrink-0", {
  variants: {
    size: {
      // 2xs ONLY for icon have issue to refactor
      "2xs": "h-6 w-6 p-0 [&>svg]:size-4",
      xxs: "h-7 w-7 p-0 [&>svg]:size-5",
      xs: "h-8 w-8 p-0",
      sm: "h-9 w-9 p-0",
      md: "h-10 w-10 p-0 [&>svg]:size-5",
      lg: "h-11 w-11 p-0",
      xl: "h-12 w-12 p-0",
      "2xl": "h-14 w-14 p-0",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

export type ButtonProps = Omit<AriaButtonOptions<"button">, "isDisabled"> &
  ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: boolean;
    ref?: Ref<HTMLButtonElement>;
  };

export type ButtonSize = "sm" | "md" | "lg" | "xxs" | "xs" | "xl" | "2xl" | null | undefined;

const Button = ({
  ref: forwardedRef,
  className,
  color,
  size,
  variant,
  asChild = false,
  icon = false,
  ...props
}: ButtonProps) => {
  const ref = useObjectRef(forwardedRef);
  const { buttonProps } = useButton({ ...props, isDisabled: props.disabled }, ref);

  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      className={cn(buttonVariants({ color, size, variant }), icon && iconButtonVariants({ size }), className)}
      ref={ref}
      {...buttonProps}
      {...props}
    />
  );
};

Button.displayName = "Button";

export default Button;
